import type { z } from "zod/v4";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { buildSeriesLabelMap } from "@/lib/ai/visualization/views/shared/label-map";
import { resolveRankOnlyAxisDirections } from "@/lib/ai/visualization/views/shared/presentation-defaults";
import { buildSeriesEntries } from "@/lib/ai/visualization/views/shared/series-entries";
import { validateTimeSeriesLikeSpec } from "@/lib/ai/visualization/views/shared/spec-validation";
import type { VizAuthoringCardDraft } from "@/lib/ai/visualization/views/shared/view-definition";
import type { timeSeriesLineAuthoringSchema } from "@/lib/ai/visualization/views/time-series-line/schema";

type TimeSeriesLineSpec = Extract<VizSpec, { type: "time-series-line" }>;
type TimeSeriesLineAuthoring = z.infer<typeof timeSeriesLineAuthoringSchema>;

/**
 * line chart は shared rule で validation できるため、その dispatch を local contract として公開する。
 */
export const validateTimeSeriesLineSpec = validateTimeSeriesLikeSpec;

/**
 * structured authoring payload を `time-series-line` 用の renderer-ready spec へ変換する。
 */
export function compileTimeSeriesLineSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<TimeSeriesLineSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: TimeSeriesLineAuthoring;
}): TimeSeriesLineSpec {
  const axisDirections = resolveRankOnlyAxisDirections(
    args.dataset,
    args.view.seriesKeys,
  );

  return {
    type: "time-series-line",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    xKey: args.view.xKey,
    seriesKeys: args.view.seriesKeys,
    componentProps: {
      xLabel: args.view.xLabel,
      ...(axisDirections ? { axisDirections } : null),
      seriesLabels: buildSeriesLabelMap({
        seriesKeys: args.view.seriesKeys,
        entries: args.view.seriesLabelEntries,
      }),
    },
  };
}

/**
 * time-series-line 用の UI element を構築する。
 */
export function buildTimeSeriesLineElement(
  args: BuildVizElementArgs<TimeSeriesLineSpec>,
): BuiltVizElement {
  const seriesLabels = args.spec.componentProps?.seriesLabels ?? {};

  return {
    elementKey: args.chartKey,
    element: {
      type: "TimeSeriesLine",
      props: {
        dataPath: args.dataPath,
        xKey: args.spec.xKey,
        series: buildSeriesEntries(
          args.dataset,
          args.spec.seriesKeys,
          seriesLabels,
        ),
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
